// ============================================
// P1.5 G-03：态 A「组件库」常驻面板（大类列表 + 搜索）
// 点击大类 / 输入搜索 → 打开态 B「组件选择大面板」（WidgetPickerPanel）
// ============================================
import { useEffect, useMemo, useState } from 'react';
import { Input, Tooltip } from 'antd';
import { SearchOutlined, RightOutlined } from '@ant-design/icons';
import { widgetRegistry } from '../../registry/widgetRegistry';
import { WIDGET_GROUPS } from '../../registry/widgetGroups';
import { groupIcon } from '../../registry/widgetIcons';
import useEditorUiStore from '../../stores/editorUiStore';
import usePluginStore from '../../plugins/plugin-manager';
import WidgetPickerPanel from './WidgetPickerPanel';

export default function ComponentPanel() {
  const [version, setVersion] = useState(0);
  const search = useEditorUiStore((s) => s.widgetPickerSearch);
  const setSearch = useEditorUiStore((s) => s.setWidgetPickerSearch);
  const openPicker = useEditorUiStore((s) => s.openWidgetPicker);
  // P1（2026-10-16）：有效分组（内置 + 分组配置 + 市场声明合并）单一来源
  const effectiveGroups = usePluginStore((s) => s.effectiveGroups);

  useEffect(() => widgetRegistry.subscribe(() => setVersion((v) => v + 1)), []);
  const counts = widgetRegistry.countByGroup();
  const total = widgetRegistry.getAll().length;
  const builtinKeys = useMemo(() => new Set(WIDGET_GROUPS.map((g) => g.key as string)), []);

  const openGroup = (key: string, placeholder?: boolean) => {
    if (placeholder) return;
    openPicker(key);
  };

  return (
    <div style={{ height: '100%', background: '#1a1a2e', display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '8px 10px 6px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <span style={{ color: '#e0e0e0', fontSize: 12, fontWeight: 600 }}>组件库</span>
        <div style={{ marginTop: 6 }}>
          <Input
            size="small"
            allowClear
            prefix={<SearchOutlined style={{ color: '#666' }} />}
            placeholder="搜索组件"
            value={search}
            onChange={(e) => {
              const v = e.target.value;
              setSearch(v);
              if (v.trim()) openPicker('all');
            }}
            style={{ background: '#101020' }}
            data-testid="component-panel-search"
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', paddingTop: 4 }}>
        {effectiveGroups.groups.map((g) => {
          const count = g.key === 'all' ? total : counts[g.key] || 0;
          // P1：空的自定义/市场派生分组自动隐藏（内置分组维持占位/计数展示）
          if (!builtinKeys.has(g.key as string) && count === 0) return null;
          const disabled = !!g.placeholder;
          return (
            <Tooltip key={g.key} title={disabled ? '后续接入（场景模板/拓扑组件/行业套件）' : `查看${g.label}组件`} placement="right">
              <div
                data-testid={`component-group-${g.key}`}
                onClick={() => openGroup(g.key, g.placeholder)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 12px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
                onMouseEnter={(e) => {
                  if (!disabled) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                }}
              >
                <span style={{ color: '#8b8ba7', fontSize: 15, width: 18, textAlign: 'center' }}>{groupIcon(g.key)}</span>
                <span style={{ color: disabled ? '#8b8ba7' : '#ccc', fontSize: 13, flex: 1 }}>{g.label}</span>
                <span style={{ color: '#5b6b85', fontSize: 11 }}>{disabled ? '敬请期待' : count}</span>
                {!disabled && <RightOutlined style={{ color: '#3b4a63', fontSize: 10 }} />}
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* 态 B 弹层（portal 到 body） */}
      <WidgetPickerPanel />
    </div>
  );
}
